/**
 * Sentry Error Tracking for Vue.js
 *
 * Only captures errors - no performance tracing (Cloudflare Analytics handles that).
 */
import * as Sentry from '@sentry/vue'
import type { App } from 'vue'
import type { Router } from 'vue-router'

export function initSentry(app: App, router: Router) {
  const dsn = import.meta.env.VITE_SENTRY_DSN

  // Skip if no DSN configured (development)
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn('[Sentry] Not configured (VITE_SENTRY_DSN not set)')
    }
    return
  }

  Sentry.init({
    app,
    dsn,

    // Prefer explicit VITE_SENTRY_ENVIRONMENT so a single production-mode
    // build can be deployed to multiple environments (preprod/prod) and
    // still tag Sentry events correctly. Falls back to Vite MODE for local
    // builds that don't bother setting the env var.
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE,

    // Integrate with Vue Router for better context
    integrations: [Sentry.browserTracingIntegration({ router })],

    // DISABLED - Cloudflare Analytics handles performance
    tracesSampleRate: 0,

    // Only capture errors, not all events
    beforeSend(event) {
      // Filter out non-error events
      if (!event.exception) {
        return null
      }
      return event
    },

    // Don't send PII
    sendDefaultPii: false,
  })

  if (import.meta.env.DEV) {
    console.warn('[Sentry] Initialized for error tracking')
  }
}
